// Shared shell for every individual report page. Wraps content in the
// StaffShell chrome + surfaces:
//   • a prominent "Back to Reports" button (top-left)
//   • two "Download PDF" / "Download Excel" buttons (top-right) when
//     the report supplies an `exportSpec`. Access is granted purely by
//     the caller's report-view permission (checked server-side on the
//     audit + data endpoints); no extra re-authentication step is required.
import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { downloadReportPdf, downloadReportXlsx } from "./exportUtils";
import { staffApi } from "@/lib/api";

export default function ReportShell({ title, subtitle, branchName, children, tab, exportSpec }) {
  const { bid } = useParams();
  const backHref = tab ? `/staff/branch/${bid}/reports?tab=${tab}` : `/staff/branch/${bid}/reports`;
  const [busy, setBusy] = useState(null); // "pdf" | "xlsx" | null

  const canExport = !!(exportSpec && Array.isArray(exportSpec.columns) && Array.isArray(exportSpec.rows));

  const runExport = async (format) => {
    if (!canExport) return;
    setBusy(format);
    // Fire-and-forget audit row so the export request is recorded even
    // if the browser cancels the download. The endpoint enforces the
    // caller's report-view permission server-side.
    try {
      staffApi.post("/security/data-export-audit", {
        report: title,
        scope: tab || null,
        date_from: exportSpec.range?.date_from || null,
        date_to: exportSpec.range?.date_to || null,
        row_count: exportSpec.rows.length,
        format,
      });
    } catch { /* audit is best-effort */ }

    try {
      if (format === "pdf") await downloadReportPdf({ title, branchName, ...exportSpec });
      else downloadReportXlsx({ title, branchName, ...exportSpec });
    } catch (e) {
      toast.error(`Could not build the ${format.toUpperCase()}. Try refreshing the report and retry.`);
      // eslint-disable-next-line no-console
      console.error(`${format} export failed`, e);
    } finally {
      setBusy(null);
    }
  };

  const requestExport = (format) => {
    if (!canExport) return;
    runExport(format);
  };

  return (
    <StaffShell branchName={branchName} context="branch">
      <div className="flex items-start gap-3 mb-6 flex-wrap">
        <Button
          asChild
          variant="outline"
          size="sm"
          className="gap-1 h-9"
          data-testid="report-back-btn"
        >
          <Link to={backHref}><ArrowLeft className="w-4 h-4" /> Back to Reports</Link>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold leading-tight">{title}</h1>
          {subtitle ? (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          ) : null}
        </div>
        {canExport && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 h-9"
              onClick={() => requestExport("pdf")}
              disabled={busy !== null}
              data-testid="report-download-pdf"
            >
              <FileDown className="w-4 h-4" />
              {busy === "pdf" ? "Preparing…" : "Download PDF"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 h-9"
              onClick={() => requestExport("xlsx")}
              disabled={busy !== null}
              data-testid="report-download-excel"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {busy === "xlsx" ? "Preparing…" : "Download Excel"}
            </Button>
          </div>
        )}
      </div>
      {children}
    </StaffShell>
  );
}
